// Local persistence for historical observations and score history.
// Architected as a thin store so it can be swapped for a real database later.

import type { Observation, HistoryPoint, CyclePhase } from './types';

const OBS_KEY = 'bchliq:observations';
const HIST_KEY = 'bchliq:history';
const PHASE_KEY = 'bchliq:phaseChanges';

interface StoredObservation extends Observation {
  t: number;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota — ignore */
  }
}

const SEEDED_KEY = 'bchliq:seededAt';

interface ServerSeriesPoint {
  t: number;
  v: Record<string, number>;
}

/**
 * Seed the local observation store from the server's 24/7 history series.
 * This lets change detection work immediately on any device using the history
 * the scheduled collector has accumulated — not just this browser's data.
 * Runs at most once every 6 hours.
 */
export async function seedFromServer(signal?: AbortSignal): Promise<void> {
  const lastSeeded = Number(read<string>(SEEDED_KEY, '0'));
  if (Date.now() - lastSeeded < 6 * 60 * 60 * 1000) return;

  try {
    const res = await fetch('/.netlify/functions/history', { signal });
    if (!res.ok) return;
    const data = (await res.json()) as { points?: ServerSeriesPoint[] };
    const points = data.points ?? [];
    if (points.length === 0) return;

    const cutoff = Date.now() - 190 * 24 * 60 * 60 * 1000;
    const seeded: StoredObservation[] = [];
    for (const p of points) {
      if (p.t < cutoff) continue;
      for (const [indicatorId, value] of Object.entries(p.v)) {
        if (typeof value === 'number') {
          seeded.push({
            indicatorId,
            value,
            timestamp: p.t,
            source: 'server-history',
            frequency: '',
            quality: 'fresh',
            t: p.t,
          });
        }
      }
    }

    // Merge with any existing local data, deduping by indicator+timestamp.
    const existing = read<StoredObservation[]>(OBS_KEY, []);
    const seen = new Set(existing.map((o) => `${o.indicatorId}:${o.t}`));
    const merged = [...existing];
    for (const o of seeded) {
      const key = `${o.indicatorId}:${o.t}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(o);
      }
    }
    merged.sort((a, b) => a.t - b.t);
    write(OBS_KEY, merged);
    write(SEEDED_KEY, String(Date.now()));
  } catch {
    /* server history unavailable — local data still works */
  }
}

/** Append a batch of observations to the time series (keeps ~180 days). */
/**
 * A reference-baseline observation is a deterministic placeholder used when no
 * real feed is available. It must NEVER be stored as history or used as a
 * comparison anchor — doing so produces phantom "changes" (e.g. a real live
 * price being compared against a fabricated baseline). We detect them by their
 * attributed source marker.
 */
function isReferenceBaseline(o: { source?: string }): boolean {
  return typeof o.source === 'string' && o.source.includes('reference baseline');
}

export function appendObservations(obs: Observation[]): void {
  const existing = read<StoredObservation[]>(OBS_KEY, []);
  const t = Date.now();
  const cutoff = t - 190 * 24 * 60 * 60 * 1000;
  const next = [
    // Purge any previously-poisoned reference-baseline points from history.
    ...existing.filter((o) => o.t >= cutoff && !isReferenceBaseline(o)),
    ...obs
      // Only persist real readings — skip nulls and reference baselines.
      .filter((o) => o.value !== null && !isReferenceBaseline(o))
      .map((o) => ({ ...o, t })),
  ];
  write(OBS_KEY, next);
}

/**
 * Get the most recent real observation strictly older than `beforeMs` ago.
 * Returns null when no genuine historical reading exists — callers must treat
 * this as "no change data yet" rather than fabricating a comparison.
 * Reference-baseline points are never used as anchors.
 */
export function getHistoricalValue(
  indicatorId: string,
  beforeMs: number,
): { value: number; t: number } | null {
  const all = read<StoredObservation[]>(OBS_KEY, [])
    .filter(
      (o) =>
        o.indicatorId === indicatorId &&
        o.value !== null &&
        !isReferenceBaseline(o),
    )
    .sort((a, b) => a.t - b.t);
  if (all.length === 0) return null;
  const target = Date.now() - beforeMs;
  // Only return a value if we actually have history reaching back to (or before)
  // the requested window. Without this, a single point from a minute ago would
  // masquerade as the "24h ago" / "30d ago" value and produce false 0% or
  // spurious changes.
  let candidate: StoredObservation | null = null;
  for (const o of all) {
    if (o.t <= target) candidate = o;
  }
  if (!candidate) return null;
  return { value: candidate.value as number, t: candidate.t };
}

/** Return the full min/max range observed for an indicator. */
export function getHistoricalRange(
  indicatorId: string,
): { min: number; max: number } | null {
  const vals = read<StoredObservation[]>(OBS_KEY, [])
    .filter(
      (o) =>
        o.indicatorId === indicatorId &&
        o.value !== null &&
        !isReferenceBaseline(o),
    )
    .map((o) => o.value as number);
  if (vals.length === 0) return null;
  return { min: Math.min(...vals), max: Math.max(...vals) };
}

export function appendHistoryPoint(point: HistoryPoint): void {
  const existing = read<HistoryPoint[]>(HIST_KEY, []);
  // throttle: at most one point per 6h
  const last = existing[existing.length - 1];
  if (last && point.t - last.t < 6 * 60 * 60 * 1000) {
    existing[existing.length - 1] = point;
  } else {
    existing.push(point);
  }
  const cutoff = point.t - 190 * 24 * 60 * 60 * 1000;
  write(
    HIST_KEY,
    existing.filter((p) => p.t >= cutoff),
  );
}

export function getHistory(): HistoryPoint[] {
  return read<HistoryPoint[]>(HIST_KEY, []);
}

/**
 * How many milliseconds of score history we actually hold (newest minus
 * oldest point). Used to decide whether a trend-based reading (e.g. the cycle
 * phase) is trustworthy yet, or whether we're still establishing a baseline.
 */
export function getHistorySpanMs(): number {
  const hist = getHistory();
  if (hist.length < 2) return 0;
  return hist[hist.length - 1].t - hist[0].t;
}

/**
 * Score change over a window (points). Returns 0 when we don't yet hold history
 * reaching back to the requested window — a 2-day-old series must NOT report
 * its 2-day change as a "30-day trend". Presenting an immature trend as flat
 * (rather than extrapolating from the oldest point) keeps signal/phase honest.
 */
export function scoreTrend(
  domain: 'liquidity' | 'bch',
  windowMs: number,
): number {
  const hist = getHistory();
  if (hist.length < 2) return 0;
  const now = hist[hist.length - 1];
  const target = now.t - windowMs;
  // Require a genuine anchor at or before the window start. No extrapolation
  // from hist[0]: if the earliest point is newer than the window, we simply
  // don't know the trend over that horizon yet.
  let past: HistoryPoint | null = null;
  for (const p of hist) {
    if (p.t <= target) past = p;
  }
  if (!past) return 0;
  return Math.round(now[domain] - past[domain]);
}

export interface PhaseChange {
  t: number;
  from: CyclePhase;
  to: CyclePhase;
}

export function recordPhase(phase: CyclePhase): PhaseChange[] {
  const changes = read<PhaseChange[]>(PHASE_KEY, []);
  const last = changes[changes.length - 1];
  if (!last) {
    changes.push({ t: Date.now(), from: phase, to: phase });
    write(PHASE_KEY, changes);
  } else if (last.to !== phase) {
    changes.push({ t: Date.now(), from: last.to, to: phase });
    write(PHASE_KEY, changes);
  }
  return changes;
}

export function getPhaseChanges(): PhaseChange[] {
  return read<PhaseChange[]>(PHASE_KEY, []);
}
